import Box from '@redbubble/design-system/src/Box';
import Button from '@redbubble/design-system/src/Button';
import * as constants from '@redbubble/design-system/src/constants';
import TextLink from '@redbubble/design-system/src/TextLink';
import { ErrorBoundary } from '@redbubble/next/error-boundary';
import { useIntl } from '@redbubble/next/intl';
import { ModalSize, useOpenModal } from '@redbubble/next/modals';
import { useEffect } from 'react';
import { DefaultInventoryItem, ProductTypeId } from 'types/shop-bff/types';
import { AlsoAvailableOnModal } from '../AlsoAvailableOnModal';
import { messages as AAOMessage } from '../AlsoAvailableOnSection';
import { styleSwitchMessages } from './messages';
import {
  ProductStyleProvider,
  useProductStyleContext,
} from './ProductStyleContext';
import {
  ModalDivider,
  ProductStyleModal,
  StatefulCollapse,
} from './ProductStyleModal';
import { ProductStyleSelectedItem } from './ProductStyleSelectedItem';
import { ProductStylesList } from './ProductStylesList';
import {
  AAOCloseRequested,
  showAAORequested,
  showMoreRequested,
} from './useProductStyleSwitcherState';
import { useProductStyleData } from './useProductSwitchData';

export interface ProductStyleSwitcherProps {
  currentProductTypeId: ProductTypeId;
  availableProducts: DefaultInventoryItem[];
  artistName: string;
  matureContent?: boolean;
}

const PopularStyles = () => {
  const intl = useIntl();
  const {
    data: { currentProductStyle, popularProductStyles },
  } = useProductStyleContext();

  if (popularProductStyles.length <= 0) return null;

  return (
    <ProductStylesList
      title={intl.formatMessage(styleSwitchMessages.popularStyles)}
      currentProductTypeId={currentProductStyle!.productTypeId}
      currentProductTypeGACode={currentProductStyle.gaCode}
      productStyles={popularProductStyles}
    />
  );
};

const MoreStyles = () => {
  const intl = useIntl();
  const {
    data: { currentProductStyle, moreProductStyles },
  } = useProductStyleContext();

  if (moreProductStyles.length === 0) return null;

  return (
    <>
      <ModalDivider />
      <ProductStylesList
        title={intl.formatMessage(styleSwitchMessages.moreStyles)}
        currentProductTypeId={currentProductStyle.productTypeId}
        currentProductTypeGACode={currentProductStyle.gaCode}
        productStyles={moreProductStyles}
      />
    </>
  );
};

const MoreHiddenStyles = () => {
  const intl = useIntl();
  const {
    data: { currentProductStyle, hiddenProductStyles },
    state,
    dispatch,
  } = useProductStyleContext();

  if (hiddenProductStyles.length === 0) return null;

  const handleLoadMoreStylesBtnClick = () => showMoreRequested(dispatch);

  return (
    <>
      {!state.styleSelectMoreOpen && (
        <Box display="flex" justifyContent="center" marginTop="l">
          <Button
            size={constants.SMALL}
            strong
            onClick={handleLoadMoreStylesBtnClick}
          >
            {intl.formatMessage(styleSwitchMessages.loadMoreStyles, {
              count: hiddenProductStyles.length,
            })}
          </Button>
        </Box>
      )}

      <StatefulCollapse open={state.styleSelectMoreOpen}>
        <ProductStylesList
          currentProductTypeId={currentProductStyle.productTypeId}
          currentProductTypeGACode={currentProductStyle.gaCode}
          productStyles={hiddenProductStyles}
        />
      </StatefulCollapse>
    </>
  );
};

const ViewMoreAAOProductsTextLink = () => {
  const intl = useIntl();
  const {
    data: { aaoProductCount },
    dispatch,
  } = useProductStyleContext();

  if (aaoProductCount <= 0) return null;

  const handleViewAAOProductsTextLinkClick = () => {
    showAAORequested(dispatch);
  };

  return (
    <>
      <ModalDivider />
      <Box display="flex" justifyContent="center" marginBottom="s">
        <TextLink onClick={handleViewAAOProductsTextLinkClick}>
          {intl.formatMessage(styleSwitchMessages.alsoAvailableOnTextLink, {
            count: aaoProductCount,
          })}
        </TextLink>
      </Box>
    </>
  );
};

const AAOModal = () => {
  const intl = useIntl();
  const {
    data: { availableProducts, matureContent, artistName },
    state: { aaoOpen },
    dispatch,
  } = useProductStyleContext();
  const openModal = useOpenModal();

  useEffect(() => {
    if (aaoOpen) {
      openModal({
        accessibleTitle: intl.formatMessage(AAOMessage.alsoAvailableOn),
        content: (
          <ErrorBoundary>
            <AlsoAvailableOnModal
              matureContent={matureContent}
              items={availableProducts}
              artistName={artistName}
            />
          </ErrorBoundary>
        ),
        size: ModalSize.Large,
        // keeps the Product Style Switcher Modal's state consistent
        onCloseRequested: () => {
          AAOCloseRequested(dispatch);
        },
      });
    }
  }, [
    aaoOpen,
    artistName,
    availableProducts,
    dispatch,
    intl,
    matureContent,
    openModal,
  ]);

  // returns null because we are optionally showing the modal content inside of the useEffect()
  return null;
};

export const ProductStyleSwitcher: React.FC<ProductStyleSwitcherProps> = ({
  currentProductTypeId,
  availableProducts,
  artistName,
  matureContent,
}) => {
  const intl = useIntl();

  const data = useProductStyleData({
    intl,
    currentProductTypeId,
    availableProducts,
    matureContent,
  });

  const { currentProductStyle } = data;

  if (!currentProductStyle) {
    return null;
  }

  return (
    <ProductStyleProvider data={{ ...data, currentProductStyle, artistName }}>
      <ProductStyleSelectedItem />
      <ProductStyleModal>
        <PopularStyles />
        <MoreStyles />
        <MoreHiddenStyles />
        <ViewMoreAAOProductsTextLink />
      </ProductStyleModal>
      <AAOModal />
    </ProductStyleProvider>
  );
};
